import React, { useEffect, useState } from 'react';
import OtpInput from "react-otp-input";
import cx from 'classnames';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { getColorsConfig } from '../../../../AssetViewer/helper/cssHelper';
import { FormWidgetContext } from '../..';
import { injectAktivFallback } from '../../../../widgets/widget.helper';

import styles from './otpInput.module.scss';
import get from 'lodash/get';


const FormOtpInputWidget = function OtpInputEditor({ className, style, widgetProps, breakpoint }) {
  const context = React.useContext(FormWidgetContext);

  const [otp, setOtp] = React.useState('');
  const [errors, setErrors] = React.useState('');

  const id = get(widgetProps, 'uid');
  const properties = get(widgetProps, 'properties', {});
  const placeholder = get(widgetProps, 'properties.desktop.placeholder') || "";
  const placeholderColor = getPlaceholderColor(properties);
  const field = get(widgetProps, 'properties.desktop.field') || "";
  const fieldId = get(widgetProps, 'content.id') || "";
  const error = get(context.errors, id);
  const errorResponse = get(context,'errors.errors[0].message')

  useEffect(() => {
    setErrors('');
    if (context.errors){
      context.errors.errors=[];
    }
    context.updateField(id, field, fieldId, otp);
  }, [otp]);

  Fonts.useFont(get(widgetProps, 'fonts') || []);

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, 'regular')
  }
  var labelStyles = getLabelStyles(properties, breakpoint, 'regular');
  var inputStyles = getInputStyles(properties, breakpoint, 'regular');

  const isBottomBorder = inputStyles?.borderBottomWidth == undefined ? false : true;
  const isBorder = inputStyles?.borderWidth == undefined ? false : true;
  let borderCondition;

  if (isBottomBorder) {
    borderCondition = {borderBottom: `${inputStyles?.borderBottomWidth} solid ${inputStyles?.borderColor}`};
  } else if (isBorder) {
    borderCondition = {border: `${inputStyles?.borderWidth} solid ${inputStyles?.borderColor}`}
  } else {
    borderCondition = {border : "none"};
  }

  const handleChange = (otp) => setOtp(otp);

  const handlePaste = (event) => {
    const pastedData = event.clipboardData.getData('text');
    const filteredData = pastedData.replace(/[^0-9]/g, '');
  
    if (filteredData !== pastedData) {
      event.preventDefault();
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
  
      input.value = input.value.slice(0, start) + filteredData + input.value.slice(end);
      input.setSelectionRange(start + filteredData.length, start + filteredData.length);
  
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);
    }
  };
  

  useEffect(()=>{
    setErrors(error);
  }, [error])

  useEffect(()=>{
    if(errorResponse) {
      setErrors(errorResponse);
    }
  }, [errorResponse])

  return (
    <div
      id={id}
      className={cx(className, styles.inputField)}
      style={widgetStyles}
      data-widget-id={id}
    >      
    {placeholderColor &&
      <style>
        {`
          #${id} input::-webkit-input-placeholder {
            color: ${placeholderColor};
          }
        `}
      </style>
    }
      <OtpInput
        value={otp}
        onChange={handleChange}
        numInputs={4}
        renderSeparator={<span style={{ width: `${labelStyles?.paddingBottom}` }}></span>}
        isInputNum={true}
        shouldAutoFocus={widgetProps.editorView ? false : true}
        inputStyle={borderCondition && {
          ...borderCondition,
          background: `${inputStyles?.background}`,
          borderBottomLeftRadius: `${inputStyles?.borderBottomLeftRadius && inputStyles?.borderBottomLeftRadius}`,
          borderBottomRightRadius: `${inputStyles?.borderBottomRightRadius && inputStyles?.borderBottomRightRadius}`,
          borderTopLeftRadius: `${inputStyles?.borderTopLeftRadius && inputStyles?.borderTopLeftRadius}`,
          borderTopRightRadius: `${inputStyles?.borderTopRightRadius && inputStyles?.borderTopRightRadius}`,
          width: `${labelStyles?.width}`,
          height: `${labelStyles?.height}`,
          fontSize: `${inputStyles?.fontSize}`,
          color: `${inputStyles?.color}`,
          fontWeight: "400",
          padding:`${inputStyles?.paddingLeft}`
        }}
        renderInput={(props, index) => (
          <input
            {...props}
            placeholder={placeholder}
            onPaste={handlePaste}
            inputMode="numeric"
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
        )}
        focusStyle={{
          outline: "none",
        }}
      />
      <span className={cx(styles.error, { [styles.active]: errors })}>{errors}</span>
    </div>
  )
}

/**
 * --------------------------------------------------
 * Field Styles
 * -------------------------------------------------- 
*/
function getFieldStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);
  const activeStyles = get(defaultProperties, `${active}`, {});

  //alignment
  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left';
  styles['alignItems'] = alignment;

  if (alignment === 'left') {
    styles['alignItems'] = 'flex-start';
  } else if (alignment === 'right') {
    styles['alignItems'] = 'flex-end';
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Label Styles
 * -------------------------------------------------- 
*/
function getLabelStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);
  const activeStyles = get(defaultProperties, `${active}`, {});

  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left';

  const fieldSpacing = get(deviceProperties, 'styles.layout.fieldSpacing') || 0;
  styles['paddingBottom'] = `${fieldSpacing}px`;

  const fieldWidth = get(deviceProperties, 'styles.layout.width') || 0;
  styles['width'] = `${fieldWidth}px`;

  const fieldHeight = get(deviceProperties, 'styles.layout.height') || 0;
  styles['height'] = `${fieldHeight}px`;

  const labelPadding = get(deviceProperties, 'styles.layout.labelPadding') || 0;
  if (alignment === 'left') {
    styles['paddingLeft'] = `${labelPadding}px`;
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${labelPadding}px`;
  }

  const inputStyles = get(defaultProperties, 'styles.regular.label');
  const fontStyles = getFontStyle(inputStyles);

  styles = {
    ...styles,
    ...fontStyles
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Input Styles
 * -------------------------------------------------- 
*/
function getInputStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);

  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left';
  const inputPadding = get(deviceProperties, 'styles.layout.inputPadding') || 0;

  if (alignment === 'left') {
    styles['paddingLeft'] = `${inputPadding}px`;
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${inputPadding}px`;
  }

  const inputStyles = get(defaultProperties, 'styles.regular.input');
  const fontStyles = getFontStyle(inputStyles);

  styles = {
    ...styles,
    ...fontStyles
  }

  const border = get(defaultProperties, 'styles.regular.border');
  const borderStyles = getBorderStyles(border);

  styles = {
    ...styles,
    ...borderStyles
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * -------------------------------------------------- 
*/
export function getPlaceholderColor(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const inputStyles = get(defaultProperties, 'styles.regular.input');

  const colorRgb = get(inputStyles, 'placeholderColor', null);
  if (!colorRgb) {
    return null;
  }

  return getColorsConfig(colorRgb);
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = get(border, 'radius.topLeft', null);
  const topRight = get(border, 'radius.topRight', null);
  const bottomLeft = get(border, 'radius.bottomLeft', null);
  const bottomRight = get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const borderStyle = get(border, 'style') || 'bottom';
  if (borderStyle !== 'none') {
    const borderWidthString = get(border, 'width') || 0;
    var borderWidget = parseInt(borderWidthString) + 'px';

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth';
    styles[fieldName] = borderWidget;

    const colorRgb = get(border, 'color');
    var color = 'transparent';
    if (colorRgb) {
      color = getColorsConfig(colorRgb);
    }

    styles['borderColor'] = color;
  }

  return styles;
}

export function getFontStyle(fontStyles) {
  var styles = {};

  if (!fontStyles) {
    return styles;
  }

  //background-color
  const backgroundColorRgb = get(fontStyles, 'background', null);
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb)
    }
  }
  
  //font-color
  const colorRgb = get(fontStyles, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = get(fontStyles, 'font.fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = get(fontStyles, `font.fontSize`) || 14;
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + 'px';
    styles['fontSize'] = fontSize;
  }

  return styles;
}

export { FormOtpInputWidget };
